'use client';

import {
  updateComplianceItem,
  updateServiceRequest,
  useGetComplianceItem,
} from '@commenda/api';
import { getClientInfoFromCookie } from '@commenda/auth';
import {
  Badge,
  Button,
  DocViewerComponent,
  IconButton,
  Icons,
  LoadingIndicator,
  MultiFileUploader,
  Sheet,
  SheetContent,
  SheetTitle,
} from '@commenda/kakashi';
import { Country, ObligationRulesCategory } from '@commenda/prisma';
import { SyntheticComplianceStatus } from '@commenda/types/src/compliance-calendar/compliances/types';
import type { UnifiedFiling } from '@commenda/types/src/compliance-calendar/compliances/unified-filing';
import { FileBasic } from '@commenda/types/src/file/types';
import { NonTimezoneSensitiveDate } from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { ComplianceModel } from '@commenda/utils/models/compliance';
import {
  ChatRoomType,
  ComplianceStatus,
  LegacyDocumentType,
  ServiceRequestStatus,
} from '@prisma/client';
import { useEffect, useState } from 'react';
import { ChatRoomTrigger } from '../../../chat/triggers/ChatRoomTrigger';
import { useNotification } from '../../../hooks/useNotification';
import { FilingStatusBadge } from '../../../misc/FilingStatusBadge';
import { InlineAssigneeCell } from './FilingsTableTnaCells';

/**
 * Right-side panel for a CLIENT-managed compliance filing.
 * Replaces navigating to the service-request page for client managed compliance items.
 */
export const ComplianceFilingDrawer = ({
  filing,
  companyId,
  incorporationCountry,
  refreshCompliances,
  onClose,
}: {
  filing: UnifiedFiling | null;
  companyId?: number;
  incorporationCountry: Country;
  refreshCompliances: () => void;
  onClose: () => void;
}) => {
  const { setNotification } = useNotification();
  const [ackFiles, setAckFiles] = useState<FileBasic[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const ackFile = ackFiles[0] ?? null;

  // A filing with no service request is discussed in its own thread. The list rows don't carry
  // the thread's id, so it comes from the filing itself.
  const hasFilingThread = Boolean(filing && !filing.serviceRequestId);
  const { data: complianceItemData } = useGetComplianceItem(
    hasFilingThread && filing ? filing.id : ''
  );
  const filingThreadCompliance =
    complianceItemData?.compliance?.id === filing?.id
      ? complianceItemData?.compliance
      : undefined;
  const userId = getClientInfoFromCookie()?.user?.id;

  // Seed the uploader from the filing's existing acknowledgement whenever the panel opens.
  useEffect(() => {
    const ack = filing?._complianceItem?.acknowledgements?.[0];
    setAckFiles(
      ack?.fileUrl
        ? [
            {
              fileName: ack.fileName,
              fileUrl: ack.fileUrl,
              documentType: LegacyDocumentType.Other,
              id: ack.id,
            },
          ]
        : []
    );
  }, [filing?.id]);

  // Default the preview open when there's a file to show; close it when the file is removed.
  useEffect(() => {
    setShowPreview(!!ackFile?.fileUrl);
  }, [ackFile?.fileUrl, filing?.id]);

  // Resolve a viewable (signed) URL — same mechanism kakashi's file preview uses: prefer an
  // already-signed URL, else sign by file id. The `#toolbar=0…` fragment hides the browser's
  // native PDF chrome so the preview reads as a clean document sheet (matches the design).
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      if (!ackFile) {
        setPreviewUrl('');
        return;
      }
      let url = ackFile.signedUrl ?? '';
      if (!url && ackFile.id)
        url = await getSignedUrlByFileId(ackFile.id, undefined);
      if (!url) url = ackFile.fileUrl ?? '';
      if (cancelled || !url) return;
      const isPdf = url.toLowerCase().includes('.pdf');
      setPreviewUrl(isPdf ? `${url}#toolbar=0&navpanes=0&view=FitH` : url);
    };
    void resolve();
    return () => {
      cancelled = true;
    };
  }, [ackFile?.id, ackFile?.signedUrl, ackFile?.fileUrl]);

  if (!filing) return null;

  const formattedDueDate = filing.dueDate
    ? NonTimezoneSensitiveDate.fromISO8601String(filing.dueDate).format(
        'MMM DD, YYYY'
      )
    : null;

  // Acknowledgement present ⟺ filing completed (upload completes, delete reverts), so reflect
  // that in the header immediately without waiting for the (snapshotted) filing prop to refresh.
  const displayStatus = ackFile ? ComplianceStatus.COMPLETED : filing.status;
  const displaySyntheticStatus = ackFile
    ? SyntheticComplianceStatus.COMPLETED
    : filing.syntheticStatus;

  const persistStatus = async (
    status: ComplianceStatus,
    acknowledgements: FileBasic[]
  ) => {
    setIsSaving(true);
    try {
      const res = await updateComplianceItem(filing.id, {
        status,
        acknowledgements,
      });
      if (!res.success) throw new Error('update failed');
      if (filing.serviceRequestId && status === ComplianceStatus.COMPLETED) {
        await updateServiceRequest({
          id: filing.serviceRequestId,
          data: { status: ServiceRequestStatus.COMPLETED },
        });
      }
      refreshCompliances();
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Could not update this filing. Please try again.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // MultiFileUploader fires this on both upload and delete with the resulting file list.
  const onAckFilesChange = (next: FileBasic[]) => {
    const uploaded = next[0];
    if (uploaded?.fileUrl) {
      void persistStatus(ComplianceStatus.COMPLETED, [
        {
          documentType: LegacyDocumentType.Other,
          fileName: uploaded.fileName,
          fileUrl: uploaded.fileUrl,
        },
      ]);
    } else {
      void persistStatus(ComplianceStatus.NOT_FILED, []);
    }
  };

  const isPreviewVisible = showPreview && !!ackFile?.fileUrl;

  return (
    <Sheet open modal={false} onOpenChange={(value) => !value && onClose()}>
      <SheetContent
        side="right"
        hideCloseIcon
        hideOverlay
        onInteractOutside={(e) => e.preventDefault()}
        className="m-xl flex h-[calc(100%-2rem)] gap-none overflow-hidden rounded-lg border border-border-primary p-none shadow-xl"
        // Standard documents-tab details sheet: a floating rounded card (no overlay, non-modal).
        // Width is set inline so it deterministically beats the Sheet variant's built-in `w-3/4`
        // (75vw) — a one-off arbitrary `max-w-[960px]` class isn't reliably generated, which left
        // the card at 75vw and the panes (480px each) flush-left with empty space on the right.
        // Preview pane + filing panel are 480px each → 960px when the preview is open, else 480px.
        style={{
          width: isPreviewVisible ? 960 : 480,
          maxWidth: 'calc(100vw - 2rem)',
        }}
      >
        {/* Document preview pane (attached to the left of the sheet when an acknowledgement exists) */}
        {isPreviewVisible && (
          <div className="flex w-[480px] min-w-0 shrink-0 flex-col bg-bg-secondary">
            {/* White top strip — continuous with the filing panel's top bar (no divider here) */}
            <div className="flex h-[56px] shrink-0 items-center border-b border-border-secondary bg-bg-primary px-3xl">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowPreview(false)}
              >
                Hide acknowledgement
              </Button>
            </div>
            {/* Divider lives on the document area only, so it starts below the top bar */}
            <div className="flex min-h-0 flex-1 overflow-auto border-r border-border-primary p-4xl">
              {previewUrl ? (
                <div className="h-full w-full overflow-hidden rounded-lg border border-border-primary bg-bg-primary shadow-sm">
                  <DocViewerComponent uri={previewUrl} />
                </div>
              ) : (
                <div className="flex h-full w-full items-center justify-center">
                  <LoadingIndicator size={32} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Filing panel */}
        <div className="flex w-[480px] shrink-0 flex-col overflow-hidden">
          {/* Top bar — the filing's chat and close */}
          <div className="flex h-[56px] shrink-0 items-center justify-end gap-md border-b border-border-secondary px-3xl">
            {filingThreadCompliance && userId && (
              <ChatRoomTrigger
                key={filing.id}
                hideLabel
                chatRoomInfo={{
                  chatRoomId: filingThreadCompliance.chatRoom?.id,
                  chatRoomType: ChatRoomType.COMPLIANCE_THREAD,
                  filingId: filing.id,
                }}
                userId={userId}
                companyId={filingThreadCompliance.company.id}
                isChatClosedOverride={
                  displayStatus === ComplianceStatus.COMPLETED ||
                  displayStatus === ComplianceStatus.FILED
                }
                includeInternalNotes={false}
              />
            )}
            <IconButton
              icon={<Icons.XClose className="size-5" />}
              aria-label="Close panel"
              variant="default"
              size="sm"
              onClick={onClose}
            />
          </div>

          {/* Title bar */}
          <div className="flex shrink-0 items-start justify-between gap-3xl border-b border-border-secondary p-3xl">
            <div className="flex min-w-px flex-col gap-md">
              <SheetTitle className="text-lg font-bold text-text-primary">
                {filing.title}
              </SheetTitle>
              <div className="flex flex-wrap items-center gap-md text-sm text-text-primary">
                <Badge variant="gray" size="sm">
                  {ComplianceModel.getFormattedComplianceCategory(
                    filing.category as ObligationRulesCategory,
                    incorporationCountry
                  )}
                </Badge>
                {filing.version ? (
                  <>
                    <span className="size-1 shrink-0 rounded-full bg-border-primary" />
                    <span>{filing.version}</span>
                  </>
                ) : null}
                {formattedDueDate ? (
                  <>
                    <span className="size-1 shrink-0 rounded-full bg-border-primary" />
                    <span>Due {formattedDueDate}</span>
                  </>
                ) : null}
              </div>
            </div>
            <FilingStatusBadge
              status={displayStatus}
              syntheticStatus={displaySyntheticStatus}
              size="lg"
            />
          </div>

          {/* Body */}
          <div className="flex flex-1 flex-col gap-3xl overflow-y-auto p-3xl">
            <div className="flex flex-col gap-xs">
              <span className="text-sm text-text-tertiary">
                Assigned to (optional)
              </span>
              <InlineAssigneeCell
                filing={filing}
                companyId={companyId}
                refreshCompliances={refreshCompliances}
              />
            </div>

            <div className="flex flex-col gap-xs">
              <span className="text-sm text-text-tertiary">
                Acknowledgement (optional)
              </span>
              <MultiFileUploader
                companyId={companyId}
                documentType={LegacyDocumentType.Other}
                files={ackFiles}
                setFiles={setAckFiles}
                onFilesChange={onAckFilesChange}
                onPreviewFile={() => setShowPreview(true)}
                onError={() =>
                  setNotification({
                    show: true,
                    valid: false,
                    title: 'Error',
                    message: 'Could not upload the file. Please try again.',
                  })
                }
                maxFiles={1}
                disabled={isSaving}
                message="PDF, DOCX, XLSX, CSV, PNG or JPG"
                accept={{
                  'application/pdf': ['.pdf', '.xlsx', '.docx', '.csv'],
                  'image/*': ['.png', '.jpg', '.jpeg'],
                }}
              />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
